import React, { useContext, useState, useEffect } from 'react'
import { FormFieldLabel } from 'components/spreadsheet/components/form/fieldLabel'
import { getRowHeightVariable, isSameValueStringValue } from 'components/spreadsheet/helpers/functions'
import { cellStyles } from 'components/spreadsheet/components/cell/styles'
import SharedCheckbox from 'components/checkbox'

interface Props {
  value: boolean | null
  align: 'center' | 'left' | 'right'
  color: string
  readOnly: boolean
  rowHeight: number
  selected: boolean
  handleKeyDown: (event: React.KeyboardEvent) => void
  setCellValue: (value: string | null) => void
  /** In a form: the platform's checkbox, at its larger size and labelled "Yes", at the field's start, rather than the grid's centred one. */
  asFormField?: boolean
  /** The column's name, which names the box. */
  label?: string
}

const styles = cellStyles()

const Checkbox: React.FC<Props> = ({
  value,
  align,
  color,
  readOnly,
  rowHeight,
  selected,
  handleKeyDown,
  setCellValue,
  asFormField,
  label
}) => {
  // In a form, named as the field it's drawn in (its name, and "Required Field" on a required one).
  const labelledBy = useContext(FormFieldLabel)
  const [currentValue, setCurrentValue] = useState<string>(
    value === undefined || value === null ? 'false' : value.toString()
  )

  useEffect(() => {
    if (!isSameValueStringValue(currentValue, value)) {
      setCurrentValue(value !== null && value !== undefined ? value.toString() : 'false')
    }
  }, [value])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentValue(event.target.checked.toString())
    setCellValue(event.target.checked.toString())
  }

  if (asFormField) {
    return (
      // The keys still reach the form's handling, which moves between fields.
      <div
        className="cell-value flex h-[34px] items-center"
        data-focussed={selected || undefined}
        onKeyDown={handleKeyDown}
      >
        <SharedCheckbox
          checked={currentValue === 'true'}
          disabled={readOnly}
          onChange={handleChange}
          label={label}
          labelledBy={labelledBy}
          className="my-0"
          size="lg"
        >
          {/* What ticking it means, and a larger target: a click on the word ticks the box too. */}
          <span className="text-body">Yes</span>
        </SharedCheckbox>
      </div>
    )
  }

  return (
    <div
      className={styles.value({ focused: selected, class: 'cell-value fit-text' })}
      data-focussed={selected || undefined}
      tabIndex={-1}
      style={{
        textAlign: align,
        color: color,
        width: 'var(--cell-width)',
        maxWidth: 'var(--cell-width)',
        maxHeight: `${getRowHeightVariable(rowHeight) - 2}px`
      }}
    >
      {/* The platform's 16px checkbox, centred in the cell (its wrapper a flex box, so no line of text around the
          box pushes it up); the keys still reach the grid's handling. */}
      <div className="flex h-full items-center justify-center [&>div]:flex" onKeyDown={handleKeyDown}>
        <SharedCheckbox
          checked={currentValue === 'true'}
          disabled={readOnly}
          onChange={handleChange}
          label={label}
          className="my-0"
        />
      </div>
    </div>
  )
}

export const CheckboxCell = Checkbox
